import { IconName } from '@grafana/ui';
import { NodeData, StructuredNodeData } from './base';
import { createContextByType, ContextTypeRegistry } from './factory';
import { ChatContextItem, ItemDataType } from './types';

const defaultIcons: Record<ItemDataType, IconName> = {
  datasource: 'database',
  label_name: 'database',
  label_value: 'database',
  dashboard: 'dashboard',
  dashboard_folder: 'folder',
  unknown: 'circle-mono',
  structured: 'gf-grid',
  component: 'gf-grid',
  entity: 'asserts',
  skill: 'play',
};

function getTitleFromContextNode(nodeData: NodeData | StructuredNodeData): string {
  return nodeData instanceof StructuredNodeData
    ? nodeData.formatForLLM().data.name
    : (nodeData.text ?? 'Given Context');
}

function getImgFromContextNode(nodeData: NodeData | StructuredNodeData): string | undefined {
  if ('img' in nodeData) {
    return (nodeData as { img?: string }).img;
  }
  return undefined;
}

/**
 * Creates a new chat context item that can be added to conversations.
 *
 * This function creates structured context items that provide additional information
 * to the AI assistant during conversations. Context items can include:
 * - Datasources (metrics, logs, traces)
 * - Labels and their values
 * - Dashboards and folders
 * - Structured data objects
 *
 * The created context item includes display metadata (title, icon, image) and
 * the underlying data that will be formatted and sent to the LLM when the
 * context is included in a conversation.
 *
 * @param type - The type of context to create (Datasource, Label, Dashboard etc.)
 * @param params - Type-specific parameters for creating the context item
 * @returns A ChatContextItem that can be added to conversation context
 *
 * @example
 * ```typescript
 * // Create a context that will be passed into the function triggering an assistant open
 * openAssistant({
 *   ...
 *   origin: 'my-feature',
 *   context: [
 *     createAssistantContextItem('datasource', {
 *       datasourceUid: datasource.uid,
 *       img: datasource.meta?.info?.logos?.small,
 *     }),
 *   ],
 * });
 * ```
 */
export function createAssistantContextItem<T extends keyof ContextTypeRegistry>(
  type: T,
  params: ContextTypeRegistry[T]['params']
): ChatContextItem {
  const nodeData = createContextByType(type, params);
  const defaultTitle = params.title ?? getTitleFromContextNode(nodeData);
  const img = params.img ?? getImgFromContextNode(nodeData);
  return {
    node: {
      id: nodeData.id,
      name: defaultTitle,
      img,
      icon: params.icon ?? defaultIcons[type],
      navigable: false,
      selectable: true,
      data: nodeData,
    },
    occurrences: [],
  };
}
